import React from 'react'
import { StyleSheet, View } from 'react-native'
import Modal from 'react-native-modal'
import { useTheme } from '@react-navigation/native'
import type { Theme } from '@theme'
import { Button, Icon, Text } from '@components'
import { useLanguage } from '@context'

// Keeps the question readable on a wide desktop screen.
const MODAL_MAX_WIDTH = 440
// Same round badge size as the plan-limit prompt (PaywallProvider).
const BADGE_SIZE = 64
// Light gold tint, like the premium card, behind a gold card icon.
const BADGE_TINT_ALPHA = '20'

export type CancelSubscriptionModalProps = {
  isVisible: boolean
  /** Last day of the paid period, already formatted; null when unknown. */
  endDate: string | null
  /** "Non" — and a tap outside the card — keeps the subscription. */
  onKeep: () => void
  /** "Oui" — the subscriber confirmed they want to stop. */
  onConfirm: () => void
}

/**
 * The "are you sure?" step in front of stopping Keeptouch Infinity: tapping
 * "Arrêter mon abonnement" never stops anything on its own. It also tells the
 * subscriber that Infinity lasts until the end of the period already paid.
 */
export const CancelSubscriptionModal = ({
  isVisible,
  endDate,
  onKeep,
  onConfirm,
}: CancelSubscriptionModalProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()

  const periodMessage = endDate
    ? t('subscription.cancelConfirmUntilDate', { date: endDate })
    : t('subscription.cancelConfirmUntilPeriodEnd')

  return (
    <Modal
      style={styles.modal}
      isVisible={isVisible}
      onBackdropPress={onKeep}
      onBackButtonPress={onKeep}
      animationIn="fadeIn"
      animationOut="fadeOut"
      statusBarTranslucent
    >
      <View style={styles.container} testID="subscription-cancel-modal">
        <View style={styles.iconBadge}>
          <Icon name="creditCard" size={theme.iconSize.jumbo} color={theme.colors.gold} />
        </View>

        <Text type="subtitleBold" center style={styles.question}>
          {t('subscription.cancelConfirmQuestion')}
        </Text>

        <Text type="bodySmall" center color={theme.colors.secondary} style={styles.message}>
          {periodMessage}
        </Text>

        <Button
          testID="subscription-cancel-keep"
          title={t('subscription.cancelConfirmNo')}
          textColor={theme.colors.white}
          style={styles.keepButton}
          containerStyle={styles.keepButtonContainer}
          onPress={onKeep}
        />
        <Button
          testID="subscription-cancel-confirm"
          title={t('subscription.cancelConfirmYes')}
          unfilled
          textColor={theme.colors.error}
          style={styles.confirmButton}
          onPress={onConfirm}
        />
      </View>
    </Modal>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    modal: {
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    container: {
      width: '100%',
      maxWidth: MODAL_MAX_WIDTH,
      alignSelf: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.medium,
      paddingHorizontal: theme.spacing.xl,
      paddingVertical: theme.spacing.xxl,
    },
    iconBadge: {
      width: BADGE_SIZE,
      height: BADGE_SIZE,
      borderRadius: BADGE_SIZE / 2,
      backgroundColor: theme.colors.gold + BADGE_TINT_ALPHA,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing.lg,
    },
    question: {
      marginHorizontal: theme.spacing.sm,
    },
    message: {
      marginHorizontal: theme.spacing.sm,
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xl,
    },
    // "Non" is the prominent choice — gold, like every Infinity call to action.
    keepButton: {
      backgroundColor: theme.colors.gold,
      borderWidth: 0,
    },
    keepButtonContainer: {
      marginBottom: theme.spacing.md,
    },
    // "Oui" stays plainly available, outlined in red since it ends Infinity.
    confirmButton: {
      borderColor: theme.colors.error,
    },
  })
